import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

type Settings = {
  enabled: boolean;
  window_seconds: number;
  per_user_limit: number;
  per_ip_limit: number;
  invalid_slug_limit: number;
  block_missing_user_agent: boolean;
  block_known_bots: boolean;
};

type Attempt = {
  id: string;
  slug: string | null;
  user_id: string | null;
  ip_hash: string | null;
  outcome: string;
  reason: string | null;
  created_at: string;
};

const BLOCKED = ["rate_limited", "bot_blocked", "guess_blocked", "invalid_slug", "not_found"];

/** Rate limiting + bot protection settings for signed blueprint PDF downloads. */
export function BlueprintPdfProtectionPanel() {
  const [cfg, setCfg] = useState<Settings | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [{ data: s }, { data: a }] = await Promise.all([
      supabase
        .from("blueprint_pdf_rate_limit_settings")
        .select(
          "enabled, window_seconds, per_user_limit, per_ip_limit, invalid_slug_limit, block_missing_user_agent, block_known_bots",
        )
        .eq("id", true)
        .maybeSingle(),
      supabase
        .from("blueprint_pdf_download_attempts")
        .select("id, slug, user_id, ip_hash, outcome, reason, created_at")
        .in("outcome", BLOCKED)
        .order("created_at", { ascending: false })
        .limit(30),
    ]);
    if (s) setCfg(s as Settings);
    setAttempts((a as Attempt[]) ?? []);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("blueprint_pdf_rate_limit_settings")
      .update({ ...cfg, updated_at: new Date().toISOString(), updated_by: user?.id ?? null })
      .eq("id", true);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Download protection updated");
  };

  const num = (key: keyof Settings, label: string) =>
    cfg && (
      <label className="space-y-1">
        <span className="font-mono text-[10px] uppercase text-muted-foreground">{label}</span>
        <Input
          type="number"
          min={1}
          value={cfg[key] as number}
          onChange={(e) => setCfg({ ...cfg, [key]: Number(e.target.value) })}
          className="h-8 text-xs"
        />
      </label>
    );

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <div>
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <ShieldAlert className="size-4 text-primary" />
          Blueprint PDF download protection
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Per-user and per-IP rate limits plus bot filtering on signed PDF downloads. Repeated
          invalid slugs from one network are blocked to stop token guessing and replay attempts.
        </p>
      </div>

      {cfg && (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            {num("window_seconds", "Window (seconds)")}
            {num("per_user_limit", "Per-user limit")}
            {num("per_ip_limit", "Per-IP limit")}
            {num("invalid_slug_limit", "Invalid-slug limit")}
          </div>
          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} />
              Protection enabled
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Switch
                checked={cfg.block_known_bots}
                onCheckedChange={(v) => setCfg({ ...cfg, block_known_bots: v })}
              />
              Block known bots
            </label>
            <label className="flex items-center gap-2 text-xs">
              <Switch
                checked={cfg.block_missing_user_agent}
                onCheckedChange={(v) => setCfg({ ...cfg, block_missing_user_agent: v })}
              />
              Block missing user agent
            </label>
          </div>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving && <Loader2 className="size-3 animate-spin mr-1" />}
            Save protection settings
          </Button>
        </>
      )}

      <div>
        <h3 className="font-mono text-[10px] uppercase text-muted-foreground mb-2">
          Recent blocked / failed attempts
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground font-mono text-[10px] uppercase">
              <tr className="border-b border-border">
                <th className="text-left py-2">When</th>
                <th className="text-left py-2">Outcome</th>
                <th className="text-left py-2">Slug</th>
                <th className="text-left py-2">IP ref</th>
                <th className="text-left py-2">Reason</th>
              </tr>
            </thead>
            <tbody>
              {attempts.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-4 text-muted-foreground">
                    No blocked download attempts recorded.
                  </td>
                </tr>
              )}
              {attempts.map((a) => (
                <tr key={a.id} className="border-b border-border/50">
                  <td className="py-2 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="py-2 font-mono text-[10px] text-destructive">{a.outcome}</td>
                  <td className="py-2 font-mono text-[10px]">{a.slug ?? "—"}</td>
                  <td className="py-2 font-mono text-[10px]">
                    {a.ip_hash ? `${a.ip_hash.slice(0, 10)}…` : "—"}
                  </td>
                  <td className="py-2 font-mono text-[10px] text-muted-foreground">{a.reason ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
